import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { HiOutlineFolderPlus, HiOutlinePhoto } from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from './ui/PageHeader';

const BlogCategory = () => {
  const baseUrl = import.meta.env.VITE_LOCAL_BASE_URL;
  const [imgUrl, setImgUrl] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState({
    name: '',
  });

  const errors = useMemo(
    () => ({
      name: !data.name.trim() ? 'Category name is required.' : '',
    }),
    [data.name],
  );

  const formHandler = (event) => {
    const { name, value } = event.target;
    setData((currentData) => ({ ...currentData, [name]: value }));
  };

  const imgHandler = (event) => {
    const file = event.target.files[0];
    setImgUrl(file || null);
  };

  const btnHandler = async (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (errors.name) {
      toast.error('Please complete the highlighted fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('name', data.name);

      if (imgUrl) {
        formData.append('image', imgUrl);
      }

      const res = await axios.post(`${baseUrl}/api/v1/blog-categories`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(res.data.message);
      setData({ name: '' });
      setImgUrl(null);
      setSubmitted(false);
      event.target.reset();
    } catch (err) {
      toast.error('Category could not be created. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="page-stack">
      <PageHeader
        eyebrow="Blog Management"
        title="Add Category"
        description="Create a clean category entry with an optional image for visual grouping."
        actions={
          <Link to="/dashboard/blog-category-list" className="btn btn-soft-secondary">
            View Categories
          </Link>
        }
      />

      <div className="surface-card form-card">
        <form method="post" onSubmit={btnHandler} noValidate>
          <div className="row g-4">
            <div className="col-12 col-lg-4">
              <label className="upload-dropzone" htmlFor="category-image">
                <span className="upload-icon">
                  <HiOutlinePhoto aria-hidden="true" />
                </span>
                <strong>{imgUrl ? imgUrl.name : 'Upload category image'}</strong>
                <small>PNG, JPG, or WebP recommended.</small>
              </label>
              <input
                type="file"
                name="image"
                id="category-image"
                className="visually-hidden"
                accept="image/*"
                onChange={imgHandler}
              />
            </div>

            <div className="col-12 col-lg-8">
              <div className="mb-4">
                <label className="form-label" htmlFor="category-name">
                  Category name
                </label>
                <input
                  type="text"
                  name="name"
                  id="category-name"
                  placeholder="Enter category name"
                  className={`form-control form-control-lg ${submitted && errors.name ? 'is-invalid' : ''}`}
                  value={data.name}
                  onChange={formHandler}
                />
                {submitted && errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                      Saving
                    </>
                  ) : (
                    <>
                      <HiOutlineFolderPlus aria-hidden="true" />
                      Add Category
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default BlogCategory;
